/**
 * DbEnvProvider (US-AIF.P0-0247-S03 · @UC9419 · @DP-5) — React context giữ env dữ liệu admin đang
 * xem (`real` | `sandbox`), PERSIST localStorage (refresh giữ lựa chọn), đồng bộ store module-level
 * (db-env.ts) mà api client + raw fetch đọc, và INVALIDATE React Query khi đổi (mọi màn refetch từ DB
 * mới chọn). Mirror ImpersonationTokenProvider nhưng CÓ persist (env không nhạy cảm như token).
 *
 * BẤT BIẾN:
 * - `real` là mặc định fail-safe: localStorage vắng/giá-trị-lạ ⇒ real (KHÔNG tự nhận sandbox).
 * - Đổi env ⇒ (1) cập nhật store để request kế mang đúng header, (2) invalidateQueries để tránh trộn
 *   dữ liệu 2 DB trên UI, (3) ghi localStorage cho lần reload sau.
 * - 🔴 US-AE-0321: state KHÔNG được sao chép sang `useState` — provider đọc THẲNG store module-level
 *   qua `useSyncExternalStore`. Nhờ vậy `resetDbEnvToReal()` gọi từ auth.ts (logout / phiên chết,
 *   NGOÀI React) làm banner ẩn NGAY. Bản sao state là lý do màn Đăng nhập từng đeo banner sandbox.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { AdminDbEnv } from "@goppy/contracts";
import {
  currentDbEnv,
  persistDbEnv,
  readPersistedDbEnv,
  setCurrentDbEnv,
  subscribeDbEnv,
  DB_ENV_STORAGE_KEY,
} from "@/lib/db-env.js";

export { DB_ENV_STORAGE_KEY };

interface DbEnvValue {
  readonly dbEnv: AdminDbEnv;
  readonly setDbEnv: (env: AdminDbEnv) => void;
  readonly isSandbox: boolean;
}

const DbEnvContext = createContext<DbEnvValue | null>(null);

/** Nạp lựa chọn đã lưu vào store — chạy MỘT lần ở render đầu, TRƯỚC khi đọc snapshot (request đầu
 * tiên của app mang đúng env). Idempotent nên StrictMode render đôi vẫn an toàn. */
function hydrateDbEnvFromStorage(): AdminDbEnv {
  const persisted = readPersistedDbEnv();
  setCurrentDbEnv(persisted);
  return persisted;
}

export function DbEnvProvider({ children }: { readonly children: ReactNode }) {
  const queryClient = useQueryClient();

  useState(hydrateDbEnvFromStorage);
  const dbEnv = useSyncExternalStore(subscribeDbEnv, currentDbEnv, currentDbEnv);

  const setDbEnv = useCallback((next: AdminDbEnv) => {
    // Persist cho lần reload sau; store sau cùng để mọi request phát ngay sau đó mang đúng header
    // (đổi store là thứ kích hoạt re-render + invalidate bên dưới).
    persistDbEnv(next);
    setCurrentDbEnv(next);
  }, []);

  // Đổi env (từ toggle HOẶC từ reset ngoài React) ⇒ invalidate toàn bộ cache → mọi màn refetch từ DB
  // mới, KHÔNG trộn dữ liệu 2 môi trường. Bỏ qua lần mount đầu (chưa có gì để làm cũ).
  const lastEnvRef = useRef<AdminDbEnv>(dbEnv);
  useEffect(() => {
    if (lastEnvRef.current === dbEnv) return;
    lastEnvRef.current = dbEnv;
    void queryClient.invalidateQueries();
  }, [dbEnv, queryClient]);

  const value = useMemo<DbEnvValue>(
    () => ({ dbEnv, setDbEnv, isSandbox: dbEnv === "sandbox" }),
    [dbEnv, setDbEnv],
  );

  return <DbEnvContext.Provider value={value}>{children}</DbEnvContext.Provider>;
}

/** Thiếu Provider = lỗi lập trình (composition root quên bọc) → throw rõ ràng thay vì fallback im lặng. */
export function useDbEnv(): DbEnvValue {
  const ctx = useContext(DbEnvContext);
  if (!ctx) throw new Error("useDbEnv phải nằm trong <DbEnvProvider>.");
  return ctx;
}

/**
 * Biến thể KHÔNG throw — trả null khi vắng Provider. Dùng cho component render ở nơi CÓ THỂ nằm ngoài
 * Provider trong test/harness cô lập (vd Topbar test mount standalone). Fail-safe: vắng Provider ⇒
 * toggle tự ẩn thay vì làm sập cả cây (App.tsx production LUÔN bọc DbEnvProvider).
 */
export function useOptionalDbEnv(): DbEnvValue | null {
  return useContext(DbEnvContext);
}
